import { mergeProps } from '@base-ui/react/merge-props'
import { useRender } from '@base-ui/react/use-render'
import { isString } from 'lodash-es'

import { cn } from '@eigtech/ui/lib/utils'

export type TypographyProps = {
  className?: string
}

export function TypographyH1({ render, className, ...props }: useRender.ComponentProps<'h1'>) {
  return useRender({
    defaultTagName: 'h1',
    render,
    props: mergeProps<'h1'>(
      {
        className: cn('scroll-m-20 text-balance text-4xl font-extrabold tracking-tight', className),
      },
      props
    ),
  })
}

export function TypographyH2({ render, className, ...props }: useRender.ComponentProps<'h2'>) {
  return useRender({
    defaultTagName: 'h2',
    render,
    props: mergeProps<'h2'>(
      {
        className: cn(
          'scroll-m-20 pb-2 text-3xl font-semibold tracking-tight first:mt-0',
          className
        ),
      },
      props
    ),
  })
}

export function TypographyH3({ render, className, ...props }: useRender.ComponentProps<'h3'>) {
  return useRender({
    defaultTagName: 'h3',
    render,
    props: mergeProps<'h3'>(
      { className: cn('scroll-m-20 text-2xl font-semibold tracking-tight', className) },
      props
    ),
  })
}

export function TypographyH4({ render, className, ...props }: useRender.ComponentProps<'h4'>) {
  return useRender({
    defaultTagName: 'h4',
    render,
    props: mergeProps<'h4'>(
      { className: cn('scroll-m-20 text-xl font-semibold tracking-tight', className) },
      props
    ),
  })
}

export function TypographyH5({ render, className, ...props }: useRender.ComponentProps<'h5'>) {
  return useRender({
    defaultTagName: 'h5',
    render,
    props: mergeProps<'h5'>(
      { className: cn('scroll-m-20 text-lg font-semibold tracking-tight', className) },
      props
    ),
  })
}

export function TypographyH6({ render, className, ...props }: useRender.ComponentProps<'h5'>) {
  return useRender({
    defaultTagName: 'h5',
    render,
    props: mergeProps<'h5'>(
      { className: cn('text-md scroll-m-20 font-semibold tracking-tight', className) },
      props
    ),
  })
}

export function TypographyP({ render, className, ...props }: useRender.ComponentProps<'p'>) {
  return useRender({
    defaultTagName: 'p',
    render,
    props: mergeProps<'p'>({ className: cn('mbe-0 not-last:mb-4 leading-7', className) }, props),
  })
}

export function TypographyBlockquote({
  render,
  className,
  ...props
}: useRender.ComponentProps<'blockquote'>) {
  return useRender({
    defaultTagName: 'blockquote',
    render,
    props: mergeProps<'blockquote'>(
      { className: cn('mt-6 border-l-2 pl-6 italic', className) },
      props
    ),
  })
}

export function TypographyList({ render, className, ...props }: useRender.ComponentProps<'ul'>) {
  return useRender({
    defaultTagName: 'ul',
    render,
    props: mergeProps<'ul'>({ className: cn('my-6 ml-6 list-disc [&>li]:mt-2', className) }, props),
  })
}

export function TypographyInlineCode({
  render,
  className,
  ...props
}: useRender.ComponentProps<'code'>) {
  return useRender({
    defaultTagName: 'code',
    render,
    props: mergeProps<'code'>(
      {
        className: cn(
          'bg-muted relative rounded px-[0.3rem] py-[0.2rem] font-mono text-sm font-semibold',
          className
        ),
      },
      props
    ),
  })
}

export function TypographyCodeBlock({
  render,
  className,
  children,
  ...props
}: useRender.ComponentProps<'pre'>) {
  return useRender({
    defaultTagName: 'pre',
    render,
    props: mergeProps<'pre'>(
      {
        className: cn('bg-muted relative rounded p-3 font-mono text-sm', className),
        children: <code>{children}</code>,
      },
      props
    ),
  })
}

export function TypographyLead({ render, className, ...props }: useRender.ComponentProps<'p'>) {
  return useRender({
    defaultTagName: 'p',
    render,
    props: mergeProps<'p'>({ className: cn('text-muted-foreground text-xl', className) }, props),
  })
}

export function TypographyLarge({ render, className, ...props }: useRender.ComponentProps<'span'>) {
  return useRender({
    defaultTagName: 'span',
    render,
    props: mergeProps<'span'>({ className: cn('text-lg font-semibold', className) }, props),
  })
}

export function TypographySmall({
  render,
  className,
  ...props
}: useRender.ComponentProps<'small'>) {
  return useRender({
    defaultTagName: 'small',
    render,
    props: mergeProps<'small'>(
      { className: cn('text-sm font-medium leading-none', className) },
      props
    ),
  })
}

export function TypographyMuted({ render, className, ...props }: useRender.ComponentProps<'span'>) {
  return useRender({
    defaultTagName: 'span',
    render,
    props: mergeProps<'span'>({ className: cn('text-muted-foreground text-sm', className) }, props),
  })
}
export function TypographyEyebrow({
  render,
  className,
  ...props
}: useRender.ComponentProps<'span'>) {
  return useRender({
    defaultTagName: 'span',
    render,
    props: mergeProps<'span'>(
      { className: cn('text-muted-foreground font-bold uppercase', className) },
      props
    ),
  })
}

export function WrapChildrenWithText({
  children,
  ...props
}: React.ComponentProps<typeof TypographyP>) {
  return typeof children === 'string' || (Array.isArray(children) && children.every(isString)) ? (
    <TypographyP {...props}>{children}</TypographyP>
  ) : (
    <>{children}</>
  )
}
